import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { buildReturnToState, ProfileBackNavState } from './useProfileBackNavigation'

/** The ongoing-loads list; the load detail page lives at `${LOADS_LIST_PATH}/:loadNumber`. */
export const LOADS_LIST_PATH = '/updater-agent/loads'

const samePath = (a: string, b: string) => {
    try {
        return decodeURIComponent(a) === decodeURIComponent(b)
    } catch {
        return a === b
    }
}

/**
 * Drops the global-search highlight from a location state before it is carried as `returnToState`.
 * The highlight hook clears it from `window.history` only, so the router's copy still holds it; replayed
 * on Back it would re-run the origin's deep-link handling (Invoices resets its tab, search and page).
 */
export const withoutSearchHighlight = (state: unknown): unknown => {
    if (!state || typeof state !== 'object' || !('globalSearchHighlightId' in state)) return state
    const rest: Record<string, unknown> = { ...(state as Record<string, unknown>) }
    delete rest.globalSearchHighlightId
    return Object.keys(rest).length ? rest : undefined
}

/**
 * The back-nav state for opening the load detail page at `targetPath` from `location`.
 *
 * Re-opening the load already on screen (the Paperwork / Edit sidebars' "Open Full Load" on the
 * detail page itself, or a global search for it) keeps the page's own back target. Stamping the page
 * as its own `returnTo` would make Back land on the same load. The same load can sit under two URLs
 * (record id from statements, rate-conf id elsewhere), so the record ids are compared too. Opening a
 * different load still chains: Back returns to the load it was opened from.
 */
export const buildLoadDetailBackState = (
    targetPath: string,
    location: { pathname: string; search: string; state?: unknown },
    targetLoadId?: string
): ProfileBackNavState => {
    const current = location.state as (ProfileBackNavState & { loadId?: string }) | null
    const isSameLoad = samePath(location.pathname, targetPath) || (!!targetLoadId && current?.loadId === targetLoadId)
    if (!isSameLoad) return buildReturnToState({ ...location, state: withoutSearchHighlight(location.state) })
    return current?.returnTo ? { returnTo: current.returnTo, returnToState: current.returnToState } : {}
}

/**
 * Opens the load detail page and stamps the caller's current location as `returnTo`, so the
 * detail header's back button returns to wherever the user came from (statement, invoices,
 * driver trips, …) instead of always landing on the loads list. Pass `backState` to override the
 * return target when the caller's location can't reproduce its own view (the driver profile tabs).
 */
export const useOpenLoadDetail = () => {
    const navigate = useNavigate()
    const location = useLocation()

    return useCallback(
        (routeId: string, loadId?: string, backState?: ProfileBackNavState) => {
            const path = `${LOADS_LIST_PATH}/${routeId}`
            navigate(path, {
                state: { ...(loadId ? { loadId } : {}), ...(backState ?? buildLoadDetailBackState(path, location, loadId)) },
            })
        },
        [navigate, location]
    )
}

/**
 * The load detail header's back link: the page the load was opened from (the state survives a refresh),
 * or the loads list for a direct URL / new tab. The label reads "Loads" only when that is where it goes.
 */
export const useLoadDetailBackLink = () => {
    const backState = useLocation().state as ProfileBackNavState | null
    const to = backState?.returnTo || LOADS_LIST_PATH
    return {
        to,
        state: backState?.returnTo ? backState.returnToState : undefined,
        label: to.split('?')[0] === LOADS_LIST_PATH ? 'Loads' : 'Back',
    }
}
